/**
 * A custom element to truncate content.
 */
export default class AiTruncate extends HTMLElement {
  /**
   * Initial truncated value.
   * @private
   *
   * @type {Boolean}
   */
  #isTruncated = false;

  /**
   * Flag for invalid configuration.
   * @private
   *
   * @type {Boolean}
   */
  #canTruncate = true;

  /**
   * Observes the specified attributes.
   * @static
   *
   * @return {array} A list of attributes to observe.
   */
  static get observedAttributes() {
    return ['truncated'];
  }

  /**
   * Create the custom element.
   */
  constructor() {
    super();

    /**
     * Holds nodes to truncate.
     *
     * @type {Array}
     */
    this.itemsToHide = [];

    /**
     * The maximum number shown when truncated.
     *
     * Refers to lines of text or child nodes.
     *
     * @type {Number}
     */
    this.maxLength = 4;
  }

  /**
   * Update truncated state.
   *
   * @param {Boolean} isTruncated Whether the content should be truncated.
   */
  set truncated(isTruncated) {
    this.#isTruncated = isTruncated;

    if (this.truncated) {
      this.buttonLabel.textContent = this.truncatedLabel;
    } else if (undefined !== this.expandedLabel) {
      this.buttonLabel.textContent = this.expandedLabel;
    }

    if (this.contentType === 'nodes') {
      this.button.setAttribute('expanded', this.truncated);
    }

    this.setAttribute('truncated', isTruncated);
  }

  /**
   * Get truncated state.
   *
   * @return {Boolean} Whether the content is currently truncated.
   */
  get truncated() {
    return this.#isTruncated;
  }

  /**
   * Handle changes to observed attributes.
   *
   * @param {String} name     The attribute name.
   * @param {String} oldValue The attribute's previous value.
   * @param {String} newValue The attribute's current value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (this.contentType === 'nodes' && name === 'truncated') {
      // Set the nodes' style attribute based on whether they should be hidden.
      this.itemsToHide.forEach((item) => {
        item.setAttribute('style', (newValue === 'true' ? 'display:none' : ''));
      });
    }
  }

  /**
   * Toggle truncated state.
   */
  toggleTruncated = () => {
    this.truncated = (!this.truncated);
  }

  /**
   * Additional setup required for truncating text that was originally in the
   * root of this element.
   */
  setUpTextTruncation = () => {
    const div = document.createElement('div');
    div.className = 'ai-truncate__contents';
    div.innerHTML = this.innerHTML;

    // Wrap inner contents in a div.
    try {
      this.replaceChildren(div);
    } catch (error) {
      console.log(error); // eslint-disable-line no-console

      /*
       * Prevent truncating text if we can't move it out of the root, since that
       * would hide the button along with the text overflow!
       */
      this.#canTruncate = false;
    }
  }

  /**
   * Get the button template and add it to this element.
   */
  setTruncateButton = () => {
    // Get the button template.
    const template = document.getElementById('hbg-truncate-button');
    const templateContent = template.content;

    // Append the button.
    try {
      this.appendChild(templateContent.cloneNode(true));
    } catch (error) {
      console.error(error); // eslint-disable-line no-console

      // Make sure we don't truncate if the button isn't present.
      this.#canTruncate = false;
    }

    if (this.#canTruncate) {
      // Get the button.
      this.button = this.querySelector('.ai-truncate__button');

      if (this.button !== null) {
        // Set the initial button label.
        this.buttonLabel = this.button.querySelector('.ai-truncate__button-label');
        this.buttonLabel.textContent = this.truncatedLabel;

        // Set the button style, using `link` as the default value.
        const buttonStyle = this.getAttribute('button-style') || 'link';

        if (['link', 'rounded'].includes(buttonStyle)) {
          this.button.classList.add(`ai-truncate__button--${buttonStyle}`);
        } else {
          // Apply the custom class value.
          this.button.classList.add(buttonStyle);
        }

        if (this.contentType === 'nodes') {
          this.button.setAttribute('expanded', this.truncated);
        }

        /*
         * "Hide" the button if we're truncating text, since CSS truncation
         * doesn't prevent the truncated content from being presented in-full
         * to assistive tech.
         */
        if (this.contentType === 'text') {
          this.button.setAttribute('aria-hidden', 'true');
          this.button.setAttribute('tabindex', '-1');
        }

        this.button.addEventListener('click', this.toggleTruncated);
      }
    }
  }

  /**
   * Called by the browser when the element is added to the page.
   */
  connectedCallback() {
    // The `label` attribute is required.
    if (!this.hasAttribute('truncated-label')) {
      console.error('<ai-truncate> requires the `truncated-label` attribute', this); // eslint-disable-line no-console

      // Prevent truncation if there are no button label attributes.
      this.#canTruncate = false;
    }

    // Get up the button label values.
    this.truncatedLabel = this.getAttribute('truncated-label');
    if (this.hasAttribute('expanded-label')) {
      this.expandedLabel = this.getAttribute('expanded-label');
    }

    // Content type: `text` (default) or `nodes`.
    this.contentType = this.getAttribute('content') || 'text';

    // Update the max number shown when truncated.
    if (this.hasAttribute('max')) {
      this.maxLength = Number(this.getAttribute('max')); // Attribute values are always strings.
    }

    if (this.contentType === 'text') {
      /*
       * Text is truncated using CSS. If the content is in the element root, we
       * need to move it to a div so we can apply the truncation styles.
       */
      if (!this.hasAttribute('selector')) {
        this.setUpTextTruncation();
      }

      // Update the CSS Custom Property only if we have a custom value.
      if (this.hasAttribute('max')) {
        this.style = `--ai-truncate-lines: ${this.maxLength}`;
      }
    }

    // Get the nodes to be truncated.
    if (this.contentType === 'nodes') {
      // Truncate nodes in the root, unless there is only one.
      const truncateRoot = (this.children.length === 1)
        // Use the only child's children.
        ? this.children.item(0)
        : this;

      this.itemsToHide = Array.from(truncateRoot.children).slice(this.maxLength);

      // Only truncate if we have more than the max items allowed.
      this.#canTruncate = (this.itemsToHide.length > 0);
    }

    if (this.#canTruncate) {
      this.setTruncateButton();

      // Set initial state.
      this.truncated = (this.getAttribute('truncated') !== 'false');
    }
  }

  /**
   * Called by the browser when the element is removed from the page.
   */
  disconnectedCallback() {
    this.removeAttribute('truncated');
    if (this?.button) {
      this.button.removeEventListener('click', this.toggleTruncated);
    }
  }
}
